<template>
  <Teleport to="body">
    <Transition name="modal">
      <div
        v-if="show"
        class="confirm-dialog-overlay fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-md"
        @click.self="onCancel"
      >
        <div
          class="confirm-dialog bg-[#171d2a] border border-white/10 rounded-[28px] shadow-2xl w-full max-w-sm mx-4 overflow-hidden"
          role="dialog"
          :aria-labelledby="titleId"
          aria-modal="true"
        >
          <!-- Header -->
          <div class="px-7 pt-8 pb-6">
            <div class="flex flex-col items-center gap-4 text-center">
              <div
                class="flex-shrink-0 w-12 h-12 rounded-2xl bg-white/5 flex items-center justify-center"
              >
                <svg
                  class="w-6 h-6 text-slate-300"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                  />
                </svg>
              </div>
              <div class="flex-1 min-w-0">
                <h3 :id="titleId" class="text-xl font-semibold tracking-tight text-white">
                  {{ dialogTitle }}
                </h3>
                <p class="mt-2 text-sm leading-relaxed text-slate-400">{{ dialogMessage }}</p>
              </div>
            </div>
          </div>

          <!-- Actions -->
          <div class="px-6 pb-6 grid grid-cols-2 gap-3">
            <button
              type="button"
              @click="onCancel"
              class="min-h-[44px] px-4 py-3 text-sm font-medium text-slate-200 bg-white/5 rounded-xl hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="button"
              @click="onConfirm"
              class="min-h-[44px] px-4 py-3 text-sm font-medium text-white bg-bochas-rojo rounded-xl hover:bg-bochas-red-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-400 focus-visible:ring-offset-2 focus-visible:ring-offset-bochas-bg transition-colors"
            >
              {{ dialogConfirmText }}
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from "vue";

const emit = defineEmits(["confirm", "cancel"]);

// Estado interno para las opciones del modal
const dialogTitle = ref("Confirmar acción");
const dialogMessage = ref("¿Estás seguro de que querés continuar?");
const dialogConfirmText = ref("Eliminar");

const show = ref(false);
const resolvePromise = ref(null);

const titleId = computed(() => `confirm-dialog-title-${Math.random().toString(36).substr(2, 9)}`);

function open(options = {}) {
  return new Promise((resolve) => {
    dialogTitle.value = options.title || "Confirmar acción";
    dialogMessage.value = options.message || "¿Estás seguro de que querés continuar?";
    dialogConfirmText.value = options.confirmText || "Eliminar";
    
    show.value = true;
    resolvePromise.value = resolve;
    document.body.style.overflow = "hidden";
  });
}

function onConfirm() {
  show.value = false;
  document.body.style.overflow = "";
  emit("confirm");
  if (resolvePromise.value) {
    resolvePromise.value(true);
    resolvePromise.value = null;
  }
}

function onCancel() {
  show.value = false;
  document.body.style.overflow = "";
  emit("cancel");
  if (resolvePromise.value) {
    resolvePromise.value(false);
    resolvePromise.value = null;
  }
}

// Keyboard support
function handleKeydown(e) {
  if (!show.value) return;
  if (e.key === "Escape") {
    onCancel();
  }
}

onMounted(() => {
  document.addEventListener("keydown", handleKeydown);
});

onUnmounted(() => {
  document.removeEventListener("keydown", handleKeydown);
  document.body.style.overflow = "";
});

defineExpose({ open });
</script>

<style scoped>
.modal-enter-active,
.modal-leave-active {
  transition: all 0.2s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-from .confirm-dialog,
.modal-leave-to .confirm-dialog {
  transform: scale(0.95) translateY(-10px);
}

.confirm-dialog {
  transition: transform 0.2s ease;
}
</style>
